// Translation keys and values for multi-language support

export type Language = 'en' | 'fr';

interface Translations {
  // Common
  common: {
    loading: string;
    save: string;
    cancel: string;
    delete: string;
    edit: string;
    create: string;
    update: string;
    back: string;
    close: string;
    confirm: string;
    search: string;
    filter: string;
    clear: string;
    export: string;
    all: string;
    yes: string;
    no: string;
    or: string;
    exceeded: string;
    achieved: string;
  };

  // Navigation
  nav: {
    dashboard: string;
    settings: string;
    recurring: string;
    backToDashboard: string;
  };

  // Auth
  auth: {
    email: string;
    password: string;
    confirmPassword: string;
    login: string;
    signup: string;
    logout: string;
    signIn: string;
    signUp: string;
    createAccount: string;
    welcomeBack: string;
    welcomeMessage: string;
    alreadyHaveAccount: string;
    dontHaveAccount: string;
    continueWith: string;
    google: string;
    signingIn: string;
    creatingAccount: string;
    // New keys
    useRealEmail: string;
    forgotPassword: string;
    resetPassword: string;
    resetPasswordDescription: string;
    sendResetEmail: string;
    sendingReset: string;
    checkYourEmail: string;
    resetEmailSent: string;
    backToSignIn: string;
  };

  // Dashboard
  dashboard: {
    title: string;
    subtitle: string;
    yearSummary: string;
    totalIncome: string;
    totalExpenses: string;
    totalSavings: string;
    income: string;
    expenses: string;
    savings: string;
    savingsTrend: string;
    expenseBreakdown: string;
    recentTransactions: string;
    transactions: string;
    quickAccess: string;
    quickAccessSubtitle: string;
    currentMonth: string;
    readyToTrack: string;
    readyMessage: string;
    goToCurrentMonth: string;
    noExpensesYet: string;
    noDataYet: string;
    startTracking: string;
    yearlyProgress: string;
    goal: string;
    of: string;
    keepGoing: string;
    goodProgress: string;
    almostThere: string;
    goalAchieved: string;
  };

  // Month View
  month: {
    monthlySalary: string;
    enterSalary: string;
    addTransaction: string;
    salary: string;
    totalGains: string;
    totalExpenses: string;
    savings: string;
    noTransactions: string;
    clickToStart: string;
    salaryAutoFilled: string;
    salaryAutoFilledMessage: string;
    defaultSalaryNote: string;
  };

  // Transactions
  transaction: {
    type: string;
    name: string;
    amount: string;
    category: string;
    date: string;
    notes: string;
    notesOptional: string;
    selectCategory: string;
    enterName: string;
    enterAmount: string;
    addNotes: string;
    income: string;
    expense: string;
    gain: string;
    deleteConfirm: string;
  };

  // Categories
  categories: {
    gain_1: string;
    gain_2: string;
    gain_3: string;
    gain_4: string;
    gain_5: string;
    gain_6: string;
    gain_7: string;
    gain_8: string;
    exp_1: string;
    exp_2: string;
    exp_3: string;
    exp_4: string;
    exp_5: string;
    exp_6: string;
    exp_7: string;
    exp_8: string;
    exp_9: string;
    exp_10: string;
  };

  // Settings
  settings: {
    title: string;
    profile: string;
    currency: string;
    language: string;
    theme: string;
    yearlyGoal: string;
    yearlyGoalSubtitle: string;
    defaultSalary: string;
    defaultSalarySubtitle: string;
    defaultSalaryPlaceholder: string;
    defaultSalaryNote: string;
    defaultSalaryInfo: string;
    defaultSalaryInfoMessage: string;
    saveSettings: string;
    saved: string;
    saving: string;
    light: string;
    dark: string;
    system: string;
  };

  // Recurring Transactions
  recurring: {
    title: string;
    subtitle: string;
    createRecurring: string;
    editRecurring: string;
    noRecurring: string;
    noRecurringMessage: string;
    frequency: string;
    daily: string;
    weekly: string;
    monthly: string;
    yearly: string;
    startDate: string;
    endDate: string;
    setEndDate: string;
    dayOfWeek: string;
    dayOfMonth: string;
    monthOfYear: string;
    upcoming: string;
    paused: string;
    active: string;
    pause: string;
    activate: string;
    deleteConfirm: string;
    infoTitle: string;
    infoAutomatic: string;
    infoPause: string;
    infoEdit: string;
    infoEndDate: string;
  };

  // Search & Filter
  filter: {
    searchPlaceholder: string;
    filters: string;
    transactionType: string;
    allCategories: string;
    quickDateFilters: string;
    today: string;
    last7Days: string;
    lastMonth: string;
    last3Months: string;
    lastYear: string;
    activeFilters: string;
    showing: string;
    net: string;
  };

  // Export
  export: {
    title: string;
    subtitle: string;
    csv: string;
    csvDescription: string;
    excel: string;
    excelDescription: string;
    exporting: string;
    exportingMessage: string;
  };

  // Errors & Validation
  errors: {
    required: string;
    invalidEmail: string;
    invalidAmount: string;
    passwordMismatch: string;
    passwordTooShort: string;
    selectCategory: string;
    enterName: string;
    enterAmount: string;
    loadError: string;
    saveError: string;
    deleteError: string;
    exportError: string;
  };

  // Days of week
  days: {
    sunday: string;
    monday: string;
    tuesday: string;
    wednesday: string;
    thursday: string;
    friday: string;
    saturday: string;
  };

  // Months
  months: {
    january: string;
    february: string;
    march: string;
    april: string;
    may: string;
    june: string;
    july: string;
    august: string;
    september: string;
    october: string;
    november: string;
    december: string;
  };
}

export const translations: Record<Language, Translations> = {
  en: {
    common: {
      loading: 'Loading',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      create: 'Create',
      update: 'Update',
      back: 'Back',
      close: 'Close',
      confirm: 'Confirm',
      search: 'Search',
      filter: 'Filter',
      clear: 'Clear',
      export: 'Export',
      all: 'All',
      yes: 'Yes',
      no: 'No',
      or: 'or',
      exceeded: 'exceeded',
      achieved: 'achieved',
    },

    nav: {
      dashboard: 'Dashboard',
      settings: 'Settings',
      recurring: 'Recurring Transactions',
      backToDashboard: 'Back to Dashboard',
    },

    auth: {
      email: 'Email Address',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      login: 'Login',
      signup: 'Sign Up',
      logout: 'Logout',
      signIn: 'Sign In',
      signUp: 'Sign Up',
      createAccount: 'Create Account',
      welcomeBack: 'Welcome back! Sign in to continue',
      welcomeMessage: 'Create your account to get started',
      alreadyHaveAccount: 'Already have an account?',
      dontHaveAccount: "Don't have an account?",
      continueWith: 'or continue with',
      google: 'Google',
      signingIn: 'Signing in...',
      creatingAccount: 'Creating account...',
      useRealEmail: "Use your real email — you'll need it to reset your password if you ever get locked out.",
      forgotPassword: 'Forgot password?',
      resetPassword: 'Reset your password',
      resetPasswordDescription: 'Enter your email and we\'ll send you a reset link.',
      sendResetEmail: 'Send reset email',
      sendingReset: 'Sending...',
      checkYourEmail: 'Check your email',
      resetEmailSent: 'A password reset link has been sent to',
      backToSignIn: 'Back to sign in',
    },

    dashboard: {
      title: 'MyBalance',
      subtitle: 'Track your finances and reach your goals',
      yearSummary: 'Year Summary',
      totalIncome: 'Total Income',
      totalExpenses: 'Total Expenses',
      totalSavings: 'Total Savings',
      income: 'Income',
      expenses: 'Expenses',
      savings: 'Savings',
      savingsTrend: 'Savings Trend',
      expenseBreakdown: 'Expense Breakdown',
      recentTransactions: 'Recent Transactions',
      transactions: 'Transactions',
      quickAccess: 'Quick Access to Months',
      quickAccessSubtitle: 'Select a month to view details',
      currentMonth: 'Current Month',
      readyToTrack: '🎉 Ready to track your finances?',
      readyMessage: 'Select a month above to start adding your income and expenses!',
      goToCurrentMonth: 'Go to Current Month',
      noExpensesYet: 'No expenses yet',
      noDataYet: 'No data available yet',
      startTracking: 'Start tracking your monthly finances to see trends',
      yearlyProgress: 'Yearly Progress',
      goal: 'Goal',
      of: 'of',
      keepGoing: 'Keep going!',
      goodProgress: 'Good progress!',
      almostThere: 'Almost there!',
      goalAchieved: 'Goal achieved!',
    },

    month: {
      monthlySalary: '💵 Monthly Salary',
      enterSalary: 'Enter your salary',
      addTransaction: 'Add Transaction',
      salary: 'Salary',
      totalGains: 'Total Gains',
      totalExpenses: 'Total Expenses',
      savings: 'Savings',
      noTransactions: 'No transactions yet',
      clickToStart: 'Click "Add Transaction" to get started',
      salaryAutoFilled: '✨ Default salary auto-filled!',
      salaryAutoFilledMessage: 'Your monthly salary of {amount} has been automatically added. You can change it below if needed.',
      defaultSalaryNote: '💡 Your default salary is {amount}. Change it in Settings if needed.',
    },

    transaction: {
      type: 'Type',
      name: 'Name',
      amount: 'Amount',
      category: 'Category',
      date: 'Date',
      notes: 'Notes',
      notesOptional: 'Notes (optional)',
      selectCategory: 'Select category...',
      enterName: 'Enter transaction name',
      enterAmount: '0.00',
      addNotes: 'Add any notes...',
      income: 'Income',
      expense: 'Expense',
      gain: 'Gain',
      deleteConfirm: 'Delete this transaction?',
    },

    categories: {
      gain_1: 'Part-time Job',
      gain_2: 'Overtime Pay',
      gain_3: 'Bonuses or Commissions',
      gain_4: 'Consulting or Contract Work',
      gain_5: 'Tips',
      gain_6: 'Online Work',
      gain_7: 'Scholarships or Stipends',
      gain_8: 'Other Income',
      exp_1: 'Utilities',
      exp_2: 'Internet',
      exp_3: 'Mobile Phone',
      exp_4: 'Insurance',
      exp_5: 'Loan Repayments',
      exp_6: 'Subscriptions',
      exp_7: 'Transportation',
      exp_8: 'Food',
      exp_9: 'Rent',
      exp_10: 'Other Expenses',
    },

    settings: {
      title: 'Settings',
      profile: '👤 Profile',
      currency: '💰 Currency',
      language: '🌍 Language',
      theme: '🎨 Theme',
      yearlyGoal: '🎯 Yearly Savings Goal',
      yearlyGoalSubtitle: 'Set a yearly savings target to track your progress',
      defaultSalary: '💵 Default Monthly Salary',
      defaultSalarySubtitle: 'Set your standard monthly salary. This will auto-fill when you open a new month, but you can adjust it manually if needed.',
      defaultSalaryPlaceholder: 'Enter your monthly salary (e.g., 5000)',
      defaultSalaryNote: "You can change this anytime, and it won't affect months you've already set up",
      defaultSalaryInfo: '💡 Tip: Combine with Recurring Transactions',
      defaultSalaryInfoMessage: "For fixed expenses like rent, internet, or subscriptions, use the Recurring Transactions feature! They'll automatically appear each month so you don't have to add them manually.",
      saveSettings: 'Save Settings',
      saved: 'Saved!',
      saving: 'Saving...',
      light: 'Light',
      dark: 'Dark',
      system: 'System',
    },

    recurring: {
      title: '🔄 Recurring Transactions',
      subtitle: 'Manage your recurring income and expenses',
      createRecurring: 'Create Recurring',
      editRecurring: 'Edit Recurring Transaction',
      noRecurring: 'No recurring transactions',
      noRecurringMessage: 'Set up recurring transactions for bills, subscriptions, and regular income.',
      frequency: 'Frequency',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      yearly: 'Yearly',
      startDate: 'Start Date',
      endDate: 'End Date',
      setEndDate: 'Set End Date (Optional)',
      dayOfWeek: 'Day of Week',
      dayOfMonth: 'Day of Month',
      monthOfYear: 'Month',
      upcoming: 'Upcoming Occurrences',
      paused: 'Paused',
      active: 'Active',
      pause: 'Pause',
      activate: 'Activate',
      deleteConfirm: 'Are you sure you want to delete this recurring transaction?',
      infoTitle: 'ℹ️ How Recurring Transactions Work',
      infoAutomatic: 'Automatic Creation: Transactions are automatically created based on the schedule you set.',
      infoPause: 'Pause Anytime: Toggle the power button to pause/resume without deleting.',
      infoEdit: "Edit Templates: Update details at any time. Changes won't affect already created transactions.",
      infoEndDate: 'Set End Dates: Optionally set an end date for recurring transactions.',
    },

    filter: {
      searchPlaceholder: 'Search transactions by name...',
      filters: 'Filters',
      transactionType: 'Transaction Type',
      allCategories: 'All Categories',
      quickDateFilters: 'Quick Date Filters',
      today: 'Today',
      last7Days: 'Last 7 Days',
      lastMonth: 'Last Month',
      last3Months: 'Last 3 Months',
      lastYear: 'Last Year',
      activeFilters: 'Active Filters:',
      showing: 'Showing',
      net: 'Net',
    },

    export: {
      title: 'Export Data',
      subtitle: 'Choose your preferred export format:',
      csv: 'CSV Format',
      csvDescription: 'Comma-separated values',
      excel: 'Excel Format',
      excelDescription: 'XLSX spreadsheet',
      exporting: 'Exporting...',
      exportingMessage: 'Please wait while we prepare your file',
    },

    errors: {
      required: 'This field is required',
      invalidEmail: 'Please enter a valid email address',
      invalidAmount: 'Please enter a valid amount',
      passwordMismatch: 'Passwords do not match',
      passwordTooShort: 'Password must be at least 6 characters',
      selectCategory: 'Please select a category',
      enterName: 'Please enter a name',
      enterAmount: 'Please enter a valid amount',
      loadError: 'Failed to load data',
      saveError: 'Failed to save changes',
      deleteError: 'Failed to delete',
      exportError: 'Failed to export data',
    },

    days: {
      sunday: 'Sunday',
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
    },

    months: {
      january: 'January',
      february: 'February',
      march: 'March',
      april: 'April',
      may: 'May',
      june: 'June',
      july: 'July',
      august: 'August',
      september: 'September',
      october: 'October',
      november: 'November',
      december: 'December',
    },
  },

  fr: {
    common: {
      loading: 'Chargement',
      save: 'Enregistrer',
      cancel: 'Annuler',
      delete: 'Supprimer',
      edit: 'Modifier',
      create: 'Créer',
      update: 'Mettre à jour',
      back: 'Retour',
      close: 'Fermer',
      confirm: 'Confirmer',
      search: 'Rechercher',
      filter: 'Filtrer',
      clear: 'Effacer',
      export: 'Exporter',
      all: 'Tous',
      yes: 'Oui',
      no: 'Non',
      or: 'ou',
      exceeded: 'dépassé',
      achieved: 'atteint',
    },

    nav: {
      dashboard: 'Tableau de bord',
      settings: 'Paramètres',
      recurring: 'Transactions récurrentes',
      backToDashboard: 'Retour au tableau de bord',
    },

    auth: {
      email: 'Adresse e-mail',
      password: 'Mot de passe',
      confirmPassword: 'Confirmer le mot de passe',
      login: 'Connexion',
      signup: "S'inscrire",
      logout: 'Déconnexion',
      signIn: 'Se connecter',
      signUp: "S'inscrire",
      createAccount: 'Créer un compte',
      welcomeBack: 'Bon retour ! Connectez-vous pour continuer',
      welcomeMessage: 'Créez votre compte pour commencer',
      alreadyHaveAccount: 'Vous avez déjà un compte ?',
      dontHaveAccount: "Vous n'avez pas de compte ?",
      continueWith: 'ou continuer avec',
      google: 'Google',
      signingIn: 'Connexion en cours...',
      creatingAccount: 'Création du compte...',
      useRealEmail: "Utilisez votre vraie adresse e-mail — vous en aurez besoin pour réinitialiser votre mot de passe.",
      forgotPassword: 'Mot de passe oublié ?',
      resetPassword: 'Réinitialiser votre mot de passe',
      resetPasswordDescription: 'Entrez votre e-mail et nous vous enverrons un lien de réinitialisation.',
      sendResetEmail: 'Envoyer le lien',
      sendingReset: 'Envoi en cours...',
      checkYourEmail: 'Vérifiez vos e-mails',
      resetEmailSent: 'Un lien de réinitialisation a été envoyé à',
      backToSignIn: 'Retour à la connexion',
    },

    dashboard: {
      title: 'MyBalance',
      subtitle: 'Suivez vos finances et atteignez vos objectifs',
      yearSummary: "Résumé de l'année",
      totalIncome: 'Revenus totaux',
      totalExpenses: 'Dépenses totales',
      totalSavings: 'Épargne totale',
      income: 'Revenus',
      expenses: 'Dépenses',
      savings: 'Épargne',
      savingsTrend: "Tendance de l'épargne",
      expenseBreakdown: 'Répartition des dépenses',
      recentTransactions: 'Transactions récentes',
      transactions: 'Transactions',
      quickAccess: 'Accès rapide aux mois',
      quickAccessSubtitle: 'Sélectionnez un mois pour voir les détails',
      currentMonth: 'Mois actuel',
      readyToTrack: '🎉 Prêt à suivre vos finances ?',
      readyMessage: 'Sélectionnez un mois ci-dessus pour commencer à ajouter vos revenus et dépenses !',
      goToCurrentMonth: 'Aller au mois actuel',
      noExpensesYet: 'Pas encore de dépenses',
      noDataYet: 'Aucune donnée disponible',
      startTracking: 'Commencez à suivre vos finances mensuelles pour voir les tendances',
      yearlyProgress: 'Progression annuelle',
      goal: 'Objectif',
      of: 'sur',
      keepGoing: 'Continuez !',
      goodProgress: 'Bon progrès !',
      almostThere: 'Presque là !',
      goalAchieved: 'Objectif atteint !',
    },

    month: {
      monthlySalary: '💵 Salaire mensuel',
      enterSalary: 'Entrez votre salaire',
      addTransaction: 'Ajouter une transaction',
      salary: 'Salaire',
      totalGains: 'Gains totaux',
      totalExpenses: 'Dépenses totales',
      savings: 'Épargne',
      noTransactions: 'Aucune transaction',
      clickToStart: 'Cliquez sur "Ajouter une transaction" pour commencer',
      salaryAutoFilled: '✨ Salaire par défaut rempli automatiquement !',
      salaryAutoFilledMessage: 'Votre salaire mensuel de {amount} a été ajouté automatiquement. Vous pouvez le modifier ci-dessous si nécessaire.',
      defaultSalaryNote: '💡 Votre salaire par défaut est {amount}. Modifiez-le dans les Paramètres si nécessaire.',
    },

    transaction: {
      type: 'Type',
      name: 'Nom',
      amount: 'Montant',
      category: 'Catégorie',
      date: 'Date',
      notes: 'Notes',
      notesOptional: 'Notes (facultatif)',
      selectCategory: 'Sélectionnez une catégorie...',
      enterName: 'Entrez le nom de la transaction',
      enterAmount: '0,00',
      addNotes: 'Ajoutez des notes...',
      income: 'Revenus',
      expense: 'Dépense',
      gain: 'Gain',
      deleteConfirm: 'Supprimer cette transaction ?',
    },

    categories: {
      gain_1: 'Travail à temps partiel',
      gain_2: 'Heures supplémentaires',
      gain_3: 'Bonus ou commissions',
      gain_4: 'Conseil ou contrat',
      gain_5: 'Pourboires',
      gain_6: 'Travail en ligne',
      gain_7: 'Bourses ou allocations',
      gain_8: 'Autres revenus',
      exp_1: 'Services publics',
      exp_2: 'Internet',
      exp_3: 'Téléphone mobile',
      exp_4: 'Assurance',
      exp_5: 'Remboursements de prêt',
      exp_6: 'Abonnements',
      exp_7: 'Transport',
      exp_8: 'Nourriture',
      exp_9: 'Loyer',
      exp_10: 'Autres dépenses',
    },

    settings: {
      title: 'Paramètres',
      profile: '👤 Profil',
      currency: '💰 Devise',
      language: '🌍 Langue',
      theme: '🎨 Thème',
      yearlyGoal: "🎯 Objectif d'épargne annuel",
      yearlyGoalSubtitle: "Définissez un objectif d'épargne annuel pour suivre vos progrès",
      defaultSalary: '💵 Salaire mensuel par défaut',
      defaultSalarySubtitle: 'Définissez votre salaire mensuel standard. Il sera automatiquement rempli lorsque vous ouvrez un nouveau mois, mais vous pouvez le modifier manuellement si nécessaire.',
      defaultSalaryPlaceholder: 'Entrez votre salaire mensuel (ex: 5000)',
      defaultSalaryNote: "Vous pouvez le modifier à tout moment, et cela n'affectera pas les mois que vous avez déjà configurés",
      defaultSalaryInfo: '💡 Astuce : Combinez avec les Transactions Récurrentes',
      defaultSalaryInfoMessage: "Pour les dépenses fixes comme le loyer, Internet ou les abonnements, utilisez la fonction Transactions Récurrentes ! Elles apparaîtront automatiquement chaque mois, vous n'aurez pas besoin de les ajouter manuellement.",
      saveSettings: 'Enregistrer les paramètres',
      saved: 'Enregistré !',
      saving: 'Enregistrement...',
      light: 'Clair',
      dark: 'Sombre',
      system: 'Système',
    },

    recurring: {
      title: '🔄 Transactions récurrentes',
      subtitle: 'Gérez vos revenus et dépenses récurrents',
      createRecurring: 'Créer récurrent',
      editRecurring: 'Modifier la transaction récurrente',
      noRecurring: 'Aucune transaction récurrente',
      noRecurringMessage: 'Configurez des transactions récurrentes pour les factures, abonnements et revenus réguliers.',
      frequency: 'Fréquence',
      daily: 'Quotidien',
      weekly: 'Hebdomadaire',
      monthly: 'Mensuel',
      yearly: 'Annuel',
      startDate: 'Date de début',
      endDate: 'Date de fin',
      setEndDate: 'Définir une date de fin (facultatif)',
      dayOfWeek: 'Jour de la semaine',
      dayOfMonth: 'Jour du mois',
      monthOfYear: 'Mois',
      upcoming: 'Prochaines occurrences',
      paused: 'En pause',
      active: 'Actif',
      pause: 'Mettre en pause',
      activate: 'Activer',
      deleteConfirm: 'Êtes-vous sûr de vouloir supprimer cette transaction récurrente ?',
      infoTitle: 'ℹ️ Comment fonctionnent les transactions récurrentes',
      infoAutomatic: 'Création automatique : Les transactions sont créées automatiquement selon le calendrier défini.',
      infoPause: "Pause à tout moment : Basculez le bouton d'alimentation pour mettre en pause/reprendre sans supprimer.",
      infoEdit: "Modifier les modèles : Mettez à jour les détails à tout moment. Les modifications n'affecteront pas les transactions déjà créées.",
      infoEndDate: 'Définir des dates de fin : Définissez éventuellement une date de fin pour les transactions récurrentes.',
    },

    filter: {
      searchPlaceholder: 'Rechercher des transactions par nom...',
      filters: 'Filtres',
      transactionType: 'Type de transaction',
      allCategories: 'Toutes les catégories',
      quickDateFilters: 'Filtres de date rapides',
      today: "Aujourd'hui",
      last7Days: '7 derniers jours',
      lastMonth: 'Dernier mois',
      last3Months: '3 derniers mois',
      lastYear: 'Dernière année',
      activeFilters: 'Filtres actifs :',
      showing: 'Affichage',
      net: 'Net',
    },

    export: {
      title: 'Exporter les données',
      subtitle: "Choisissez votre format d'exportation préféré :",
      csv: 'Format CSV',
      csvDescription: 'Valeurs séparées par des virgules',
      excel: 'Format Excel',
      excelDescription: 'Feuille de calcul XLSX',
      exporting: 'Exportation...',
      exportingMessage: 'Veuillez patienter pendant que nous préparons votre fichier',
    },

    errors: {
      required: 'Ce champ est obligatoire',
      invalidEmail: 'Veuillez entrer une adresse e-mail valide',
      invalidAmount: 'Veuillez entrer un montant valide',
      passwordMismatch: 'Les mots de passe ne correspondent pas',
      passwordTooShort: 'Le mot de passe doit contenir au moins 6 caractères',
      selectCategory: 'Veuillez sélectionner une catégorie',
      enterName: 'Veuillez entrer un nom',
      enterAmount: 'Veuillez entrer un montant valide',
      loadError: 'Échec du chargement des données',
      saveError: "Échec de l'enregistrement des modifications",
      deleteError: 'Échec de la suppression',
      exportError: "Échec de l'exportation des données",
    },

    days: {
      sunday: 'Dimanche',
      monday: 'Lundi',
      tuesday: 'Mardi',
      wednesday: 'Mercredi',
      thursday: 'Jeudi',
      friday: 'Vendredi',
      saturday: 'Samedi',
    },

    months: {
      january: 'Janvier',
      february: 'Février',
      march: 'Mars',
      april: 'Avril',
      may: 'Mai',
      june: 'Juin',
      july: 'Juillet',
      august: 'Août',
      september: 'Septembre',
      october: 'Octobre',
      november: 'Novembre',
      december: 'Décembre',
    },
  },
};

// Helper function to get translation
export function t(key: string, language: Language = 'en'): string {
  const keys = key.split('.');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let value: any = translations[language];

  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
  }

  return typeof value === 'string' ? value : key;
}